import { VERSION as DATABRICKS_AUTH_SDK_VERSION } from "@databricks/sdk-auth";
import type { DatabricksCertificationEvidence } from "./certification.js";

/** Exact modular Databricks SDK release used by every generated service client. */
export const DATABRICKS_SDK_VERSION = DATABRICKS_AUTH_SDK_VERSION;

export const DATABRICKS_API_VERSIONS = {
  sdkJs: DATABRICKS_SDK_VERSION,
  jobs: "2.2",
  sqlStatements: "2.0",
  unityCatalog: "2.1",
  workspace: "2.0",
  mlflow: "2.0",
  lakebaseCredentials: "2.0",
  apps: "2.0",
  responses: "agent/v1/responses",
  aiSearch: "2.0",
  serving: "2.0",
  lakeflow: "2.0",
  genie: "2.0",
  mcp: "2.0",
  supervisorAgents: "v1 beta",
  managedMemory: "2.1 beta",
} as const;

export const DATABRICKS_PACKAGE_COMPATIBILITY = {
  node: ">=22.0.0",
  package: "@fabric-harness/databricks",
  packageRange: ">=7.0.0 <8",
  pg: "^8.11.0 (only for Lakebase)",
} as const;

export const DATABRICKS_AUTH_MODES = [
  { mode: "oauth-m2m", production: true, use: "External services and CI service principals." },
  { mode: "app-service-principal", production: true, use: "Databricks Apps runtime identity." },
  {
    mode: "on-behalf-of",
    production: true,
    use: "Per-user Unity Catalog enforcement when the App forwards user identity.",
  },
  { mode: "pat", production: false, use: "Developer smoke tests and transitional automation." },
] as const;

export type DatabricksCloud = "aws" | "azure" | "gcp" | string;

export interface DatabricksWorkspaceCompatibilityRecord {
  evidenceId: string;
  certifiedAt: string;
  cloud: DatabricksCloud;
  region: string;
  workspaceHost: string;
  workspaceId?: string;
  authMode: string;
  principalKind?: string;
  runtimeVersion: string;
  packageVersion: string;
  artifactSha256: string;
  apiVersions: Record<string, string>;
  passedChecks: string[];
  appPrerequisites: string[];
  lakebasePrerequisites: string[];
}

/** Convert successful, fully identified certification evidence into a publishable matrix row. */
export function databricksCompatibilityRecord(
  evidence: DatabricksCertificationEvidence,
): DatabricksWorkspaceCompatibilityRecord {
  if (!evidence.passed)
    throw new Error(
      "Only passing Databricks certification evidence can produce a compatibility record.",
    );
  const required: Array<[string, string | undefined]> = [
    ["workspaceCloud", evidence.workspaceCloud],
    ["workspaceRegion", evidence.workspaceRegion],
    ["authMode", evidence.authMode],
    ["runtimeVersion", evidence.runtimeVersion],
    ["packageVersion", evidence.packageVersion],
    ["artifactSha256", evidence.artifactSha256],
  ];
  const missing = required.filter(([, value]) => !value).map(([name]) => name);
  if (missing.length)
    throw new Error(
      `Certification evidence is missing compatibility fields: ${missing.join(", ")}.`,
    );
  const {
    workspaceCloud,
    workspaceRegion,
    authMode,
    runtimeVersion,
    packageVersion,
    artifactSha256,
  } = evidence;
  if (
    !workspaceCloud ||
    !workspaceRegion ||
    !authMode ||
    !runtimeVersion ||
    !packageVersion ||
    !artifactSha256
  ) {
    throw new Error("Certification evidence is missing compatibility fields.");
  }
  return {
    evidenceId: evidence.evidenceId,
    certifiedAt: evidence.generatedAt,
    cloud: workspaceCloud,
    region: workspaceRegion,
    workspaceHost: evidence.workspaceHost,
    ...(evidence.workspaceId ? { workspaceId: evidence.workspaceId } : {}),
    authMode,
    ...(evidence.principalKind ? { principalKind: evidence.principalKind } : {}),
    runtimeVersion,
    packageVersion,
    artifactSha256,
    apiVersions: { ...evidence.apiVersions },
    passedChecks: [
      ...new Set(
        evidence.results
          .filter((result) => result.status === "passed")
          .flatMap((result) => [
            result.id,
            ...(Array.isArray(result.details?.evidenceChecks)
              ? result.details.evidenceChecks.filter(
                  (check): check is string => typeof check === "string",
                )
              : []),
          ]),
      ),
    ].sort(),
    appPrerequisites: [
      "Databricks Apps enabled",
      "service principal resource permissions",
      "durable store configured for restart recovery",
    ],
    lakebasePrerequisites: [
      "Lakebase Autoscaling endpoint",
      "database credential exchange permission",
      "private connectivity and trusted CA when required",
    ],
  };
}
